import { clsx } from '@alexis/helpers/clsx';
import { throttle } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { getPriceOptionsState, getTranslationsState } from '@redux/selectors';

import { HotelRateAmenity } from '@wegoTypes/hotels/metasearch/hotelRateAmenity';
import { Translations } from '@wegoTypes/translations';

import styles from './HotelStickyRatesFilter.module.scss';

interface HotelRatesFilterProps {
  filteredRateAmenities: Array<HotelRateAmenity>;
  selectedRateAmenityIds: Array<number>;
  handleRateAmenityFilterToggle: (rateAmenityId: number, isSelected: boolean) => void;
  handleOnShowTotalPriceChanged: (showTotalPrice: boolean) => void;
}

const RATES_FILTER_MARGIN_TOP = 16;
const HOTEL_TITLE_BOTTOM_POSITION = 241;
const RATES_FILTER_BOTTOM_POSITION = 829;
const ROOMS_DEAL_MARGIN_TOP = 48;

const HotelStickyRatesFilter: React.FC<HotelRatesFilterProps> = ({
  filteredRateAmenities,
  selectedRateAmenityIds,
  handleRateAmenityFilterToggle,
  handleOnShowTotalPriceChanged,
}) => {
  const priceOptions: PriceOptions = useSelector(getPriceOptionsState);
  const translations: Translations = useSelector(getTranslationsState);

  const [isSticky, setIsSticky] = useState<boolean>(false);
  const ratesFilterContainer = useRef<HTMLDivElement>(null);
  const [isDisplaySticky, setIsDisplaySticky] = useState<boolean>(false);

  useEffect(() => {
    const searchLayoutHeight = document.getElementById('hotelSearchLayout')?.clientHeight || 0;
    const hotelAnchorTabsHeight = document.getElementById('hotelAnchorTabs')?.clientHeight || 0;

    const handleScroll = () => {
      const RatesFilterCurrent = document.getElementById('RatesFilter');
      const GuestRatingCurrent = document.getElementById('GuestRating');
      const HotelInfoSectionCurrent = document.getElementById('HotelInfoSection');

      const ratesFilterPosition =
        RatesFilterCurrent?.offsetTop && RatesFilterCurrent?.offsetTop > 0
          ? RatesFilterCurrent.offsetTop + RATES_FILTER_MARGIN_TOP
          : RATES_FILTER_BOTTOM_POSITION;

      const guestRatingPosition =
        GuestRatingCurrent?.offsetTop && GuestRatingCurrent?.offsetTop > 0
          ? GuestRatingCurrent.offsetTop - RATES_FILTER_MARGIN_TOP
          : RATES_FILTER_BOTTOM_POSITION;

      const hotelInfoSectionPosition =
        HotelInfoSectionCurrent?.offsetTop && HotelInfoSectionCurrent?.offsetTop > 0
          ? HotelInfoSectionCurrent.offsetTop + ROOMS_DEAL_MARGIN_TOP
          : HOTEL_TITLE_BOTTOM_POSITION;

      if (window.scrollY > hotelInfoSectionPosition) {
        setIsDisplaySticky(true);
      } else {
        setIsDisplaySticky(false);
      }

      if (
        window.scrollY + searchLayoutHeight + hotelAnchorTabsHeight > ratesFilterPosition &&
        window.scrollY + searchLayoutHeight + hotelAnchorTabsHeight < guestRatingPosition
      ) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    const throttledHandleScroll = throttle(handleScroll, 200);

    window.addEventListener('scroll', throttledHandleScroll);

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [setIsSticky, ratesFilterContainer]);

  return (
    <>
      {isDisplaySticky && (
        <div className={clsx(styles.fixed, styles.visible, !isSticky && styles.hidden)}>
          <div className={clsx(styles.rateAmenitiesFilterAndPriceOptions)}>
            <div className={styles.rateAmenitiesFilter}>
              <div className={styles.rateAmenities}>
                {filteredRateAmenities.map((rateAmenity) => (
                  <div
                    key={rateAmenity.id}
                    className={clsx(
                      styles.rateAmenity,
                      selectedRateAmenityIds.includes(rateAmenity.id) && styles.selected,
                    )}
                    onClick={() =>
                      handleRateAmenityFilterToggle(
                        rateAmenity.id,
                        !selectedRateAmenityIds.includes(rateAmenity.id),
                      )
                    }
                  >
                    {rateAmenity.name}
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.priceOptions}>
              {translations.show_deals_by}

              <div
                className={styles.toggle}
                onClick={() => handleOnShowTotalPriceChanged(!priceOptions.showTotalPrice)}
              >
                <div
                  className={clsx(styles.option, !priceOptions.showTotalPrice && styles.selected)}
                >
                  {translations.hotel_per_night}
                </div>
                <div
                  className={clsx(styles.option, priceOptions.showTotalPrice && styles.selected)}
                >
                  {translations.total_stay}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HotelStickyRatesFilter;
